import { Parameters } from '@app/models/Parameters';
import { post, get } from './http.api';
import {
  PreClearanceRequest,
  PCComment,
  PreClearanceDocumentRequest,
  PreClearanceVerifiedDocumentRequest,
} from '@app/models/pre_clearance/PreClearanceRequest';
import {
  PreClearanceTableData,
  PreClearanceTableRow,
  PreClearanceResponse,
  PreClearanceApiData,
  UploadDocumentResponse,
  PreClearanceVerifiedDocumentResponse,
  ExtendPreClearanceResponse,
  ExtendedPreClearanceResponse,
  CategoryOptionsResponse,
} from '@app/models/pre_clearance/PreClearanceResponse';
import { DropdownOption } from '@app/models/DropdownOption';

export const getPreClearanceTableData = async (parameters: Parameters): Promise<PreClearanceTableData> => {
  try {
    const { data } = await get<any>('pre_clearances', { params: parameters }, true);
    const paginated = data?.pre_clearances ?? {};

    const preClearances: PreClearanceTableRow[] = (paginated.data ?? []).map((pc: any) => ({
      customer_name:
        pc?.customer?.name ?? ([pc?.customer?.first_name, pc?.customer?.last_name].filter(Boolean).join(' ') || '-'),
      id: pc?.id,
      reference_code: pc?.code,
      status: pc?.status,
      status_label: pc?.status_label,
      product_category_name: pc?.product_category?.name ?? '-',
      product_name: pc?.product?.name ?? '-',
      department_name: pc?.department?.name ?? '-',
      location_name: pc?.location?.name ?? '-',
      invoice_no: pc?.invoice_no ?? '-',
      hs_code: pc?.hs_code,
    }));

    return {
      data: preClearances,
      status_options: data?.status_options,
      product_category_options: data?.product_category_options,
      pagination: {
        current: paginated.current_page,
        pageSize: paginated.per_page,
        total: paginated.total,
      },
    };
  } catch (error: any) {
    console.error('Error fetching PreClearances:', error);
    throw error;
  }
};

export const getAllPreClearances = async (): Promise<PreClearanceResponse> => {
  try {
    const { data, status } = await get<PreClearanceResponse>(`pre_clearances`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${JSON.stringify(data)}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const getPreClearanceById = async (id: number): Promise<PreClearanceResponse> => {
  try {
    const { data, status } = await get<PreClearanceResponse>(`pre_clearances/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${JSON.stringify(data)}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const getProducts = async (productCategoryId: number | string): Promise<CategoryOptionsResponse> => {
  try {
    const { data } = await get<CategoryOptionsResponse>(
      `pre_clearances/get_data_by_category_id?product_category_id=${productCategoryId}`,
      undefined,
      true,
    );
    return {
      product_options: data?.product_options ?? [],
      department_options: data?.department_options ?? [],
    };
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const storePreClearance = async (PreClearanceData: PreClearanceRequest) => {
  try {
    const response = await post<any>('pre_clearances/create', PreClearanceData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const createPreClearance = async (): Promise<PreClearanceApiData> => {
  try {
    const { data, status } = await get<PreClearanceApiData>(`pre_clearances/create`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const showPreClearance = async (id: number) => {
  try {
    const { data, status } = await get<PreClearanceApiData>(`pre_clearances/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const editPreClearance = async (id: number) => {
  try {
    const { data, status } = await get<PreClearanceApiData>(`pre_clearances/${id}/edit`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const updatePreClearance = async (id: number, Updatecategory: PreClearanceRequest) => {
  try {
    const { data, status } = await post<PreClearanceRequest>(
      `pre_clearances/${id}/edit`,
      Updatecategory,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const saveConfigInfo = async (id: number, Updateconfig: any) => {
  try {
    const { data, status } = await post<any>(`pre_clearances/${id}/update_config`, Updateconfig, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const getPreClearances = async (filters?: { category_id?: number }) => {
  try {
    const { data, status } = await get<any>('pre_clearances', { params: filters }, true);
    console.log(`Status: ${status}`, `Data:`, JSON.stringify(data, null, 2));
    return data;
  } catch (error: any) {
    console.error('Error fetching PreClearances:', error);
    throw error;
  }
};

export const updateStatus = async (id: number, action: any) => {
  try {
    const { data, status } = await post<PreClearanceRequest>(`pre_clearances/${id}/do_action`, action, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const uploadDocument = async (
  pre_clearance_id: number,
  formData: FormData,
): Promise<UploadDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadDocumentResponse>(
      `pre_clearances/${pre_clearance_id}/upload_document`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};

export const storeComment = async (id: number, commentData: PCComment) => {
  try {
    const response = await post<any>(`pre_clearances/${id}/store_comment`, commentData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const updateComment = async (id: number, com_id: number, commentData: PCComment) => {
  try {
    const { data, status } = await post<PCComment>(
      `pre_clearances/${id}/${com_id}/update_comment`,
      commentData,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const generateDocument = async (id: number, documentData: PreClearanceDocumentRequest) => {
  try {
    const { data, status } = await post<PreClearanceDocumentRequest>(
      `pre_clearances/${id}/document`,
      documentData,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const getDocument = async (id: number, documentData: PreClearanceDocumentRequest) => {
  try {
    const { data, status } = await get<PreClearanceDocumentRequest>(
      `pre_clearances/${id}/get_document`,
      { params: documentData },
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
export const uploadVerifiedDocument = async (
  id: number,
  documentData: PreClearanceVerifiedDocumentRequest,
  pre_clearance_file: number,
): Promise<PreClearanceVerifiedDocumentResponse> => {
  try {
    const formData = new FormData();
    formData.append('type', documentData.type);
    formData.append('file', documentData.file);

    const { data, status } = await post<PreClearanceVerifiedDocumentResponse>(
      `pre_clearances/${id}/pre_clearance_files/${pre_clearance_file}/upload_verified`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const extendPreClearance = async (id: number, extensionData: FormData) => {
  try {
    const { data, status } = await post<ExtendPreClearanceResponse>(
      `pre_clearances/${id}/extend`,
      extensionData, // The file data for extension_req
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const approveExtension = async (id: number, extensionData: { extension_exp: string }) => {
  try {
    const { data, status } = await post<ExtendedPreClearanceResponse>(
      `pre_clearances/${id}/pre_clearance_extention/${id}/extension_approve`,
      extensionData, // New expiration date
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const pcExtensionWorkFlow = async (id: number, action: any, msg?: string) => {
  try {
    const { data, status } = await post<ExtendedPreClearanceResponse>(
      `pre_clearances/${id}/pre_clearance_extention/${id}/do_action`,
      { action, msg },
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const rejectPreClearance = async (id: number) => {
  try {
    const { data, status } = await post<ExtendedPreClearanceResponse>(
      `pre_clearances/${id}/reject`,
      {}, // No additional data required for rejection
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
